import { formatThaiDate } from './date.js';
import { formatNumber } from './numbers.js';

const DEFAULT_DATE_OPTIONS = Object.freeze({
  timeZone: 'Asia/Bangkok',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function isPlainObject(value) {
  if (value === null || Object.prototype.toString.call(value) !== '[object Object]') return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === null || Object.getPrototypeOf(prototype) === null;
}

function validateOptions(options) {
  if (!isPlainObject(options)) throw new TypeError('options must be a plain object');
  const digits = Object.hasOwn(options, 'digits') ? options.digits : 4;
  if (!Number.isSafeInteger(digits) || digits <= 0) {
    throw new RangeError('digits must be a positive safe integer');
  }
  const dateOptions = Object.hasOwn(options, 'dateOptions') ? options.dateOptions : DEFAULT_DATE_OPTIONS;
  if (!isPlainObject(dateOptions)) throw new TypeError('options.dateOptions must be a plain object');
  return { digits, dateOptions: { ...dateOptions } };
}

export function normalizeRows(rows, options = {}) {
  if (!Array.isArray(rows)) throw new TypeError('rows must be an array');
  const { digits, dateOptions } = validateOptions(options);

  return rows.map((row, index) => {
    if (!isPlainObject(row)) throw new TypeError(`rows[${index}] must be a plain object`);
    if (typeof row.label !== 'string' || row.label.trim() === '') {
      throw new TypeError(`rows[${index}].label must be a non-empty string`);
    }
    if (!Object.hasOwn(row, 'number')) throw new TypeError(`rows[${index}].number is required`);
    if (!Object.hasOwn(row, 'date')) throw new TypeError(`rows[${index}].date is required`);
    if (Object.hasOwn(row, 'metadata') && !isPlainObject(row.metadata)) {
      throw new TypeError(`rows[${index}].metadata must be a plain object`);
    }

    let number;
    let date;
    try {
      number = formatNumber(row.number, digits);
    } catch (error) {
      throw new TypeError(`rows[${index}].number: ${error.message}`);
    }
    try {
      date = formatThaiDate(row.date, dateOptions);
    } catch (error) {
      throw new TypeError(`rows[${index}].date: ${error.message}`);
    }

    const normalized = { label: row.label, number, date };
    if (Object.hasOwn(row, 'metadata')) normalized.metadata = { ...row.metadata };
    return normalized;
  });
}

function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

export function createAccessibleTable(rows, options = {}) {
  if (!isPlainObject(options)) throw new TypeError('options must be a plain object');
  const caption = options.caption ?? 'Formatted data';
  const classPrefix = options.classPrefix ?? 'amb1234';
  if (typeof caption !== 'string' || caption.trim() === '') {
    throw new TypeError('caption must be a non-empty string');
  }
  if (typeof classPrefix !== 'string' || !/^[A-Za-z][A-Za-z0-9_-]*$/.test(classPrefix)) {
    throw new TypeError('classPrefix must start with a letter and contain only letters, numbers, underscores, or hyphens');
  }

  const normalized = normalizeRows(rows, options);
  const body = normalized.map((row) => (
    `<tr><th scope="row">${escapeHtml(row.label)}</th><td>${escapeHtml(row.number)}</td><td>${escapeHtml(row.date)}</td></tr>`
  )).join('');

  return `<table class="${escapeHtml(classPrefix)}-table"><caption>${escapeHtml(caption)}</caption><thead><tr><th scope="col">Label</th><th scope="col">Number</th><th scope="col">Date</th></tr></thead><tbody>${body}</tbody></table>`;
}
